import { useEffect, useState, useContext, useMemo } from 'react';
import { AuthContext } from '../../contexts/auth';
import { InvestimentosContext } from '../../contexts/investimento';
import { DividendosContext } from '../../contexts/dividendos';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '../../components/AppLayout';
import { CardIcon } from '../../components/CardIcon';
import UpcomingDividends from '../../components/UpcomingDividends';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { CurrencyInput } from '../../components/ui/currency-input';
import { formatCurrency as formatCurrencyUtil } from '../../Utils/locale';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  TrendingUp,
  DollarSign,
  PieChart as PieChartIcon,
  Calendar,
  Plus,
  Trash2,
  Edit
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { toast } from 'sonner';
import { getUSDQuote } from '../../api/currency';
import api from '../../api/axios';

const INVEST_TYPE_LABELS = {
  'Ações': { pt: 'Ações', en: 'Stocks', es: 'Acciones' },
  'FII': { pt: 'Fundo Imobiliário (FII)', en: 'Real Estate Fund', es: 'Fondo Inmobiliario (FII)' },
  'ETF': { pt: 'ETF', en: 'ETF', es: 'ETF' },
  'Renda Fixa': { pt: 'Renda Fixa', en: 'Fixed Income' },
  'Criptomoedas': { pt: 'Criptomoedas', en: 'Crypto', es: 'Criptomonedas'},
  'Outros': { pt: 'Outros', en: 'Others', es: 'Otros' },
};

const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

function Investimentos() {
  const toNumber = (value) => {
  const n = Number(value);
  return isNaN(n) ? 0 : n;
};

  const { user } = useContext(AuthContext);
  const { saveInvestimentos, investimentos, getInvestimentos, excluirInvestimento, updateInvestimentoValues } = useContext(InvestimentosContext);
  const { dividendos, getDividendos } = useContext(DividendosContext);
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [usdQuote, setUsdQuote] = useState(null);
  const [upcomingDividendsTotal, setUpcomingDividendsTotal] = useState(0);

  // Form states para investimento
  const [invest, setInvest] = useState('');
  const [ativo, setAtivo] = useState('');
  const [moeda, setMoeda] = useState('BRL');
  const [quantidade, setQuantidade] = useState('');
  const [valorInvestido, setValorInvestido] = useState('');
  const [precoAtual, setPrecoAtual] = useState('');


  useEffect(() => {
    if (user?.id) {
      getInvestimentos(user.id);
      getDividendos(user.id);
      loadUSDQuote();
      loadUpcomingDividendsTotal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loadUSDQuote = async () => {
    try {
      const quote = await getUSDQuote();
      setUsdQuote(quote);
    } catch (error) {
      console.error('Erro ao carregar cotação do dólar:', error);
      setUsdQuote({ bid: 6.10 }); // Fallback
    }
  };

  const loadUpcomingDividendsTotal = async () => {
    try {
      const response = await api.get('/upcoming-dividends');
      setUpcomingDividendsTotal(response.data.totalExpected || 0);
    } catch (error) {
      console.error('❌ Erro ao carregar total de próximos dividendos:', error);
      setUpcomingDividendsTotal(0);
    }
  };

  const USD_RATE = usdQuote?.bid || 6.10;

  // Calcular totais por moeda
  const metrics = useMemo(() => {
    const totalBRL = investimentos
      .filter(inv => inv.moeda)
      .reduce((sum, inv) => sum + toNumber(inv.investedAmount ?? inv.valorinvestido), 0);

    const totalUSD = investimentos
      .filter(inv => inv.moeda)
      .reduce((sum, inv) => sum + toNumber(inv.investedAmount ?? inv.valorinvestido), 0);

    const totalInvestidoBRL = totalBRL + (totalUSD * USD_RATE);

    // Renda passiva dos dividendos
    const rendaMensalBRL = dividendos
      .filter(div => div.moeda === 'BRL' || !div.moeda)
      .reduce((sum, div) => sum + parseFloat(div.valor || 0), 0);

    const rendaMensalUSD = dividendos
      .filter(div => div.moeda === 'USD')
      .reduce((sum, div) => sum + parseFloat(div.valor || 0), 0);

    const rendaMensalTotalBRL = rendaMensalBRL + (rendaMensalUSD * USD_RATE);

    // Próximos proventos (dados reais do endpoint /upcoming-dividends)
    const proximosProventos = upcomingDividendsTotal;

    return {
      totalBRL,
      totalUSD,
      totalInvestidoBRL,
      rendaMensalBRL,
      rendaMensalUSD,
      rendaMensalTotalBRL,
      proximosProventos,
      totalAtivos: investimentos.length,
      ativosBRL: investimentos.filter(i => i.moeda === 'BRL' || !i.moeda).length,
      ativosUSD: investimentos.filter(i => i.moeda === 'USD').length
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investimentos, dividendos, upcomingDividendsTotal]);

  // Projeção de renda (mock - últimos 6 meses)
  const projecaoRenda = useMemo(() => {
    const months = [];
    const today = new Date();

    for (let i = 5; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const locale = language === 'pt'
            ? 'pt-BR'
            : language === 'es'
              ? 'es-ES'
              : 'en-US';

        const monthName = date.toLocaleDateString(locale, {
          month: 'short',
        });

      // Mock data - em produção, usar dados reais
      months.push({
        mes: monthName,
        BRL: metrics.rendaMensalBRL * (1 + (i * 0.02)),
        'USD (convertido)': metrics.rendaMensalUSD * USD_RATE * (1 + (i * 0.02))
      });
    }

    return months;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metrics, language]);

  // Projeção futura (mock - até 2030)
  const projecaoFutura = useMemo(() => {
    const years = [];
    const currentYear = new Date().getFullYear();
    const baseValue = metrics.totalInvestidoBRL;

    for (let i = 0; i <= 5; i++) {
      const year = currentYear + i;
      const valorTotal = baseValue * Math.pow(1.08, i); // 8% ao ano
      const rendaTotal = valorTotal * 0.006; // 0.6% ao mês

      years.push({
        ano: year.toString(),
        valorTotal: parseFloat(valorTotal.toFixed(2)),
        rendaTotal: parseFloat(rendaTotal.toFixed(2))
      });
    }

    return years;
  }, [metrics]);

  const formatCurrency = (value, currency = 'BRL') => formatCurrencyUtil(value, currency);

  const openDialog = (investimento = null) => {
    if (investimento) {
      setIsEditMode(true);
      setEditId(investimento._id);
      setInvest(investimento.invest || '');
      setAtivo(investimento.ativo || '');
      setMoeda(investimento.moeda || 'BRL');
      setQuantidade(investimento.quantidade?.toString() || '');
      setValorInvestido(investimento.investedAmount || investimento.valorinvestido || '');
      setPrecoAtual(investimento.precoatual || '');
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setIsEditMode(false);
    setEditId(null);
    setInvest('');
    setAtivo('');
    setMoeda('BRL');
    setQuantidade('');
    setValorInvestido('');
    setPrecoAtual('');
  };

  const handleSubmit = async () => {
    if (!invest || !ativo || !valorInvestido) {
      toast.error(t('fillRequiredFields'));
      return;
    }

    const data = {
      userId: user.id,
      invest,
      ativo,
      moeda,
      quantidade: quantidade ? parseFloat(quantidade) : 1,
      investedAmount: Number(valorInvestido),
      precoatual: precoAtual ? Number(precoAtual) : Number(valorInvestido)
    };

    try {
      if (isEditMode) {
        await updateInvestimentoValues({ ...data, _id: editId });
        toast.success(t('investmentUpdated'));
      } else {
        await saveInvestimentos(data);
        toast.success(t('investmentAdded'));
      }
      closeDialog();
    } catch (err) {
      console.error('Erro ao salvar investimento:', err);
      toast.error(t('errorSavingInvestment'));
    }
  };

  const handleDelete = async (id) => {
    const confirme = window.confirm(t('confirmDeleteInvestment'));
    if (confirme) {
      try {
        await excluirInvestimento(id, user.id);
        toast.success(t('investmentDeleted'));
      } catch (err) {
        console.error('Erro ao excluir investimento:', err);
        toast.error(t('errorDeletingInvestment'));
      }
    }
  };

  const calculateRentabilidade = (valorInvestido, precoAtual) => {
    if (!precoAtual || !valorInvestido || valorInvestido === 0) return 0;
    return ((precoAtual - valorInvestido) / valorInvestido) * 100;
  };

  return (
    <AppLayout
      title={t('investments')}
      subtitle={t('investmentsSubtitle')}
      icon={TrendingUp}
      headerActions={
          <div className="flex items-center gap-4">
            <div className="bg-white px-4 py-3 rounded-lg border shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-yellow-600" />
                  <span className="text-xs text-slate-500">
                   {t('usdRate')}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-slate-900 font-semibold">R$ {USD_RATE.toFixed(2)}</span>
                  {usdQuote && usdQuote.pctChange !== undefined && (
                    <span className={`text-xs ${usdQuote.pctChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {usdQuote.pctChange > 0 ? '+' : ''}{usdQuote.pctChange.toFixed(2)}%
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  onClick={() => openDialog()}
                >
                  <Plus className="w-4 h-4 mr-2" />
                   {t('addAsset')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>
                    {isEditMode
                      ? t('editInvestment')
                      : t('addNewInvestment')}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('assetType')}</Label>
                    <Select value={invest} onValueChange={setInvest}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('selectType')}/>
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(INVEST_TYPE_LABELS).map(([key, labels]) => (
                          <SelectItem key={key} value={key}>{labels[language] || key}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('assetName')}</Label>
                    <Input
                      placeholder="Ex: ITSA4, AAPL, BTC..."
                      value={ativo}
                      onChange={(e) => setAtivo(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('currency')}</Label>
                    <Select value={moeda} onValueChange={setMoeda}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BRL">BRL (Real)</SelectItem>
                        <SelectItem value="USD">USD ({t('dollar')})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('quantity')}</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={quantidade}
                        onChange={(e) => setQuantidade(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>{t('investedValue')}</Label>
                      <CurrencyInput
                        value={valorInvestido}
                        onValueChange={(val) => setValorInvestido(val)}
                        language={language}
                        placeholder={t('currencyPlaceholder')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('currentPrice')}</Label>
                    <CurrencyInput
                      value={precoAtual}
                      onValueChange={(val) => setPrecoAtual(val)}
                      language={language}
                     placeholder={t('currencyPlaceholder')}
                    />
                  </div>

                  {valorInvestido && (
                    <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">{t('totalInvested')}</span>
                          <span className="text-slate-900 font-semibold">
                            {formatCurrency(safeNumber(valorInvestido), moeda)}
                          </span>
                        </div>
                        {precoAtual && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">{t('returnLabel')}</span>
                            <span className={
                              calculateRentabilidade(safeNumber(valorInvestido), safeNumber(precoAtual)) >= 0
                                ? 'text-green-600 font-semibold'
                                : 'text-red-600 font-semibold'
                            }>
                              {calculateRentabilidade(safeNumber(valorInvestido), safeNumber(precoAtual)).toFixed(2)}%
                            </span>
                          </div>
                        )}
                      </div>
                    </Card>
                  )}

                  <Button
                    className="w-full"
                    onClick={handleSubmit}
                  >
                    {isEditMode
                      ? t('update')
                      : t('add')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
      }
      mainClassName="space-y-6"
    >
          {/* USD Quote Banner */}
          {usdQuote && (
            <Card className="p-4 bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 border-yellow-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <CardIcon icon={DollarSign} className="bg-yellow-500" iconClassName="text-white" />
                  <div>
                    <p className="text-xs text-slate-600 mb-1">
                     {t('usdBrlRealtimeQuote')}
                    </p>
                    <div className="flex items-center gap-4">
                      <div>
                        <span className="text-sm text-slate-600">{t('bid')}</span>
                        <span className="text-lg font-bold text-slate-900 ml-2">R$ {safeNumber(usdQuote.bid).toFixed(4)}</span>
                      </div>
                      <div>
                        <span className="text-sm text-slate-600">{t('ask')}</span>
                        <span className="text-lg font-bold text-slate-900 ml-2">R$ {safeNumber(usdQuote.ask).toFixed(4)}</span>
                      </div>
                      {usdQuote.pctChange !== undefined && (
                        <div className={`flex items-center px-3 py-1 rounded-full ${usdQuote.pctChange > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {usdQuote.pctChange > 0 ? '+' : ''}{usdQuote.pctChange.toFixed(2)}%
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right text-xs text-slate-500">
                  <p>{t('high')} (BRL) {safeNumber(usdQuote.high).toFixed(4)}</p>
                  <p>{t('low')} (BRL) {safeNumber(usdQuote.low).toFixed(4)}</p>
                </div>
              </div>
            </Card>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 min-[1200px]:grid-cols-4">
            <Card className="min-w-0 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <div className="mb-3 flex min-w-0 items-start gap-3">
                <CardIcon icon={DollarSign} className="bg-blue-600" iconClassName="text-white" />
                <p className="min-w-0 pt-0.5 text-sm text-slate-600 [overflow-wrap:anywhere]">
                  {t('totalEquity')}
                </p>
              </div>
              <p className="text-slate-900 font-bold mb-1">{formatCurrency(metrics.totalInvestidoBRL, 'BRL')}</p>
              <div className="flex items-center justify-between text-xs mt-2">
                <span className="text-slate-600">BRL: {formatCurrency(metrics.totalBRL, 'BRL')}</span>
                <span className="text-slate-600">USD: {formatCurrency(metrics.totalUSD, 'USD')}</span>
              </div>
            </Card>

           {metrics.rendaMensalTotalBRL > 0 && (
                <Card className="min-w-0 p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                  <div className="mb-3 flex min-w-0 items-start gap-3">
                    <CardIcon icon={TrendingUp} className="bg-green-600" iconClassName="text-white" />
                    <p className="min-w-0 pt-0.5 text-sm text-slate-600 [overflow-wrap:anywhere]">
                      {t('monthlyPassiveIncome')}
                    </p>
                  </div>

                  <p className="text-slate-900 font-bold mb-1">
                    {formatCurrency(metrics.rendaMensalTotalBRL, 'BRL')}
                  </p>

                  <div className="flex items-center justify-between text-xs mt-2">
                    <span className="text-slate-600">
                      BRL: {formatCurrency(metrics.rendaMensalBRL, 'BRL')}
                    </span>
                    <span className="text-slate-600">
                      USD: {formatCurrency(metrics.rendaMensalUSD, 'USD')}
                    </span>
                  </div>
                </Card>
              )}


            <Card className="min-w-0 p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
              <div className="mb-3 flex min-w-0 items-start gap-3">
                <CardIcon icon={Calendar} className="bg-purple-600" iconClassName="text-white" />
                <p className="min-w-0 pt-0.5 text-sm text-slate-600 [overflow-wrap:anywhere]">
                 {t('nextDividends')}
                </p>
              </div>
              <p className="text-slate-900 font-bold mb-1">{formatCurrency(metrics.proximosProventos, 'BRL')}</p>
              <p className="text-sm text-slate-600">
                {t('comingSoon')}
              </p>
            </Card>

            <Card className="min-w-0 p-6 bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
              <div className="mb-3 flex min-w-0 items-start gap-3">
                <CardIcon icon={PieChartIcon} className="bg-orange-600" iconClassName="text-white" />
                <p className="min-w-0 pt-0.5 text-sm text-slate-600 [overflow-wrap:anywhere]">
                 {t('assetsInPortfolio')}
                </p>
              </div>
              <p className="text-slate-900 font-bold mb-1">{metrics.totalAtivos}</p>
              <div className="flex items-center justify-between text-xs mt-2">
                <span className="text-slate-600">BRL: {metrics.ativosBRL}</span>
                <span className="text-slate-600">USD: {metrics.ativosUSD}</span>
              </div>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="investimentos" className="space-y-6">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="investimentos">
               {t('myInvestments')}
              </TabsTrigger>
              <TabsTrigger value="dividendos">
                {t('dividendsAndIncome')}
              </TabsTrigger>
              <TabsTrigger value="projecao">
                {t('futureProjection')}
              </TabsTrigger>
            </TabsList>

            {/* Investimentos Tab */}
            <TabsContent value="investimentos" className="space-y-6">
              <Card className="overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>{t('type')}</TableHead>
                      <TableHead>{t('asset')}</TableHead>
                      <TableHead>{t('currency')}</TableHead>
                      <TableHead>{t('quantity')}</TableHead>
                      <TableHead>{t('investedValue')}</TableHead>
                      <TableHead>{t('currentPrice')}</TableHead>
                      <TableHead>{t('return')}</TableHead>
                      <TableHead className="text-right">{t('actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {investimentos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-slate-500 py-8">
                          {t('noInvestmentsRegistered')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      investimentos.map((inv) => {
                        const investedValue = inv.investedAmount || inv.valorinvestido || 0;
                        const rentabilidade = calculateRentabilidade(
                          investedValue,
                          inv.precoatual || investedValue
                        );

                        return (
                          <TableRow key={inv._id} className="hover:bg-slate-50">
                            <TableCell>
                              <Badge variant="outline">{INVEST_TYPE_LABELS[inv.invest]?.[language] || inv.invest}</Badge>
                            </TableCell>
                            <TableCell className="font-medium">{inv.ativo}</TableCell>
                            <TableCell>
                              <Badge className={inv.moeda === 'BRL' || !inv.moeda ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                                {inv.moeda || 'BRL'}
                              </Badge>
                            </TableCell>
                            <TableCell>{inv.quantidade || 1}</TableCell>
                            <TableCell>{formatCurrency(investedValue, inv.moeda || 'BRL')}</TableCell>
                            <TableCell>{formatCurrency(inv.precoatual || investedValue, inv.moeda || 'BRL')}</TableCell>
                            <TableCell>
                              <span className={rentabilidade >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {rentabilidade >= 0 ? '+' : ''}{rentabilidade.toFixed(2)}%
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openDialog(inv)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDelete(inv._id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* Dividendos Tab */}
            <TabsContent value="dividendos" className="space-y-6">
              {/* Próximos Dividendos */}
              <UpcomingDividends />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-6">
                  <h3 className="text-slate-900 font-semibold mb-6">
                    {t('dividendsNext6Months')}
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={projecaoRenda}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="mes" stroke="#64748b" />
                      <YAxis stroke="#64748b" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Bar dataKey="BRL" fill="#3b82f6" radius={[8, 8, 0, 0]} name="BRL" />
                      <Bar dataKey="USD (convertido)" fill="#10b981" radius={[8, 8, 0, 0]} name="USD (convertido)" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>

                <Card className="overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>{t('asset')}</TableHead>
                        <TableHead>{t('currency')}</TableHead>
                        <TableHead>{t('amount')}</TableHead>
                        <TableHead>{t('payment')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dividendos.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                            {t('noDividendsRegistered')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        dividendos.map((div) => (
                          <TableRow key={div._id} className="hover:bg-slate-50">
                            <TableCell className="font-medium">{div.invest || div.ativo}</TableCell>
                            <TableCell>
                              <Badge className={div.moeda === 'BRL' || !div.moeda ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                                {div.moeda || 'BRL'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div>
                                <div>{formatCurrency(div.valor, div.moeda || 'BRL')}</div>
                                {div.moeda === 'USD' && (
                                  <div className="text-xs text-slate-500">
                                    ({formatCurrency(div.valor * USD_RATE, 'BRL')})
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {div.dataPagamento
                                  ? new Date(div.dataPagamento).toLocaleDateString(
                                      language === 'pt'
                                        ? 'pt-BR'
                                        : language === 'es'
                                          ? 'es-ES'
                                          : 'en-US'
                                    )
                                  : '-'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            </TabsContent>

            {/* Projeção Tab */}
            <TabsContent value="projecao" className="space-y-6">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-slate-900 font-semibold mb-1">
                      {t('projectedEvolution2030')}
                    </h3>
                    <p className="text-slate-600 text-sm">
                     {t('averageReturnDisclaimer')}
                    </p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={projecaoFutura}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="ano" stroke="#64748b" />
                    <YAxis stroke="#64748b" />
                    <Tooltip
                      formatter={(value) => formatCurrency(value, 'BRL')}
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="valorTotal"
                      stroke="#8b5cf6"
                      strokeWidth={3}
                      dot={{ fill: '#8b5cf6', r: 5 }}
                      name={t('totalEquityBRL')}
                    />
                    <Line
                      type="monotone"
                      dataKey="rendaTotal"
                      stroke="#10b981"
                      strokeWidth={3}
                      dot={{ fill: '#10b981', r: 5 }}
                      name={t('passiveIncome2030')}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {projecaoFutura.length > 0 && (
                  <>
                    <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50">
                      <h4 className="text-slate-600 font-medium mb-2">
                         {t('goal2030')}
                      </h4>
                      <p className="text-slate-900 font-bold text-2xl mb-1">
                      {formatCurrency(projecaoFutura[projecaoFutura.length - 1].valorTotal, 'BRL')}
                      </p>
                      <p className="text-sm text-slate-600">
                         {t('projectedEquity')}
                      </p>
                    </Card>

                    <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50">
                      <h4 className="text-slate-600 font-medium mb-2">
                         {t('passiveIncome2030')}
                      </h4>
                      <p className="text-slate-900 font-bold text-2xl mb-1">
                         {formatCurrency(projecaoFutura[projecaoFutura.length - 1].rendaTotal, 'BRL')}/
                            {language === 'pt'
                              ? 'mês'
                              : language === 'es'
                                ? 'mes'
                                : 'mo'}
                      </p>
                      <p className="text-sm text-slate-600">
                        {t('projectedMonthlyIncome')}
                      </p>
                    </Card>

                    <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50">
                      <h4 className="text-slate-600 font-medium mb-2">
                          {t('totalGrowth')}
                      </h4>
                      <p className="text-slate-900 font-bold text-2xl mb-1">
                        {metrics.totalInvestidoBRL > 0
                          ? `+${(((projecaoFutura[projecaoFutura.length - 1].valorTotal - metrics.totalInvestidoBRL) / metrics.totalInvestidoBRL) * 100).toFixed(0)}%`
                          : '-'}
                      </p>
                      <p className="text-sm text-slate-600">
                        {t('until2030')}
                      </p>
                    </Card>
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
    </AppLayout>
  );
}

export default Investimentos;
